import axios, { AxiosRequestConfig, AxiosError, AxiosResponse } from 'axios';
import { message } from 'antd';
import AdminConfig from '../config';
import { getToken } from '../utils/cookie';
import store from '../store/index';
import { baseTipInfo, setBaseTipInfo } from '@/store/baseData/baseTip';
import { realnameInfo, setRealnameInfo } from '@/store/baseData/realnameStatus';
import { toSigns } from '@/utils/HmacSHA';
import Fn from '../utils/fn';
import VoiceFn from '@/views/Voice/fn';
import domainFn from './defence';
import { Base64 } from 'js-base64';
// import { returnOptions } from './urlTimeoutConfig';
// import { defaults, updateLayoutSettings } from '../store/module/setting';
// import { redirectList, redirectListJson } from './redirectConfig';
// import { createBrowserHistory } from 'history';
// import { setLocalStorage } from '@/utils/localStorage';
// import { urlList, urlListJson } from './worldLegendConfig';

const TYPE = '/collect/collect';
const str = localStorage.getItem('BASE_TIP_INFO');
const tipInfo = str ? JSON.parse(str) : baseTipInfo;
const blankUrls = ['/box_serverlist', '.txt', '/box/server/pc_list', 'modlist_'];

// 有协议就直接用，没有就补上（默认 https）
function getNormalizedUrl(urlString?: string): URL {
  const normalized =
    urlString && /^https?:\/\//i.test(urlString)
      ? urlString
      : urlString && urlString.startsWith('//')
      ? 'https:' + urlString
      : 'https://' + urlString;
  return new URL(normalized);
}

// 检测是否为文件上传请求
function isFileUploadRequest(config: AxiosRequestConfig): boolean {
  if (!config.data) return false;
  // 检查是否为 FormData
  if (config.data instanceof FormData) return true;
  // 检查Content-Type
  const contentType = config.headers?.['Content-Type'] || config.headers?.['content-type'];
  if (contentType && contentType.includes('multipart/form-data')) return true;
  return false;
}

// 提取文件上传metaData
function extractMetaData(data: any): string {
  if (!data) return '';
  // 如果FormData，尝试获取meta 字段
  if (data instanceof FormData) {
    const meta = data.get('meta') || data.get('metaData');
    if (meta) {
      return typeof meta === 'string' ? meta : JSON.stringify(meta);
    }
  }
  // 如果是普通对象，尝试获取 meta metaData 字段
  if (typeof data === 'object' && data !== null) {
    const meta = data.meta || data.metaData;
    if (meta) {
      return typeof meta === 'string' ? meta : JSON.stringify(meta);
    }
  }
  return '';
}

// 构造请求绝对地址（包含查询参数）
export function buildRequestPath(url: URL, params?: any, method?: string): string {
  let path = url.pathname;

  // GET请求需要将params编码后拼接到pathname
  if (method?.toUpperCase() === 'GET' && params) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        queryParams.append(key, String(params[key]));
      }
    });
    const queryString = queryParams.toString();
    if (queryString) {
      path += '?' + queryString;
    }
  }

  return path;
}

// 处理请求体，返回字符串形式
function getRequestBody(data: any, isFileUpload: boolean): string {
  if (isFileUpload) {
    // 文件上传使用 metaData
    const metaData = extractMetaData(data);
    if (metaData) {
      // 限制长度300000字符以内
      return metaData.length > 300000 ? metaData.substring(0, 300000) : metaData;
    }
    return '';
  }

  // POST请求转为JSON字符串
  if (data === null || data === undefined) {
    return '';
  }

  // 如果已经是字符串，检查是否是双重序列化的 JSON 字符串
  if (typeof data === 'string') {
    // 如果字符串看起来是被 JSON.stringify 处理过的（包含转义引号），尝试解析一次
    if (data.startsWith('"') && data.includes('\\"')) {
      try {
        const parsed = JSON.parse(data);
        // 如果解析后还是字符串，说明确实是双重序列化，返回解析后的字符串
        if (typeof parsed === 'string') {
          return parsed.length > 300000 ? parsed.substring(0, 300000) : parsed;
        }
        // 如果解析后是对象，重新序列化为字符串
        const jsonStr = JSON.stringify(parsed);
        return jsonStr.length > 300000 ? jsonStr.substring(0, 300000) : jsonStr;
      } catch (e) {
        // 解析失败，说明不是有效的 JSON，直接返回原字符串
      }
    }
    return data.length > 300000 ? data.substring(0, 300000) : data;
  }

  // 转为JSON字符串
  const jsonStr = JSON.stringify(data);
  // const jsonStr = data;
  // 限制长度300000字符以内
  return jsonStr.length > 300000 ? jsonStr.substring(0, 300000) : jsonStr;
}

const needUploadCode = {
  50001: true,
  100: true,
  101: true,
  300: true,
  301: true
};

// header参数集合
export function headerBoxAgent(obj: any) {
  const param = Object.keys(obj);
  const data = Object.values(obj);
  let sign = '';
  param.forEach((item, index) => {
    const val = data[index] === undefined ? '' : data[index];
    sign += `${item}:${val};`;
  });
  return sign;
}

interface ResponseData<T> {
  code: number;

  data: T;

  msg: string;
}

// 指定 axios 请求类型

axios.defaults.headers = {
  'Content-Type': 'application/json;charset=utf-8'
};

axios.defaults.validateStatus = function (status) {
  return status < 500;
};

axios.defaults.timeout = 15000;

// 添加请求拦截器
axios.interceptors.request.use(
  async (config: AxiosRequestConfig) => {
    const token = getToken();
    const routeStatus = window.location.href.indexOf(AdminConfig.loginRouter) !== -1;
    const updaterStatus = window.location.href.indexOf(AdminConfig.updaterRouter) !== -1;
    if (tipInfo.visible && !routeStatus && !updaterStatus) {
      return config;
    }
    if (!token && !routeStatus && !updaterStatus) {
      console.clear();
      console.log(config, 'response11111111111');
      console.log(token, routeStatus, updaterStatus, '请求发送token 登录失效3');
      store.dispatch(
        setBaseTipInfo({
          visible: true,
          title: '系统提示',
          message: '登录失效，请重新登录',
          saveText: '重新登录 '
        })
      );
      sessionStorage.setItem('isExpiration', '1');
      return config;
    }
    // 获取用户token，用于校验
    /* eslint-disable  no-param-reassign */
    const strVersion: any = localStorage.getItem('appv')?.replace(/&.+/, '');
    let numVersion = strVersion.replace(/[^0-9]/g, '');
    const mainVersion = numVersion.slice(0, 3);
    const isVersion = Number(mainVersion) >= 350;
    const status = Fn.compareVersion(strVersion || '', '5.0.1');
    const hasBoxAuth = status >= 0;
    const url = getNormalizedUrl(config.url);
    const method = config.method?.toUpperCase() || '';
    // 构造请求绝对地址（包含查询参数）
    const requestPath = buildRequestPath(url, config.params, method);

    const signObj = {
      device: 'pc',
      channel: '',
      appv: localStorage.getItem('appv')?.replace(/&.+/, '') || '',
      deviceOs: localStorage.getItem('os') || '',
      deviceId: localStorage.getItem('mac') || '',
      deviceBrand: '',
      deviceModel: '',
      netType: '',
      gameOwner: '',
      isSimulator: false
    };

    const newSignObj = {
      method: method || '',
      url: method?.toUpperCase() === 'GET' ? decodeURIComponent(requestPath) || '' : requestPath || '',
      deviceType: 'pc', //设备类型
      deviceId: localStorage.getItem('mac') || '',
      appv: localStorage.getItem('appv')?.replace(/&.+/, ''),
      token: token || ''
    };

    // 区分是否是区服列表获取，区服列表不加header
    // 贪玩专区游戏区服列表判断'/box/server/pc_list'
    if (
      config.url &&
      config.url.indexOf('/box_serverlist') < 0 &&
      config.url.indexOf('.txt') < 0 &&
      config.url.indexOf('/box/server/pc_list') < 0
    ) {
      if (config.url.indexOf('v1/newLog') < 0 && config.url.indexOf('v1/logs') < 0) {
        config.headers['box-agent'] = headerBoxAgent(signObj);
      }
      config.headers.device = 'pc';
      config.headers.deviceId = localStorage.getItem('mac') || '';
      config.headers.appv = localStorage.getItem('appv')
        ? (localStorage.getItem('appv') as string).replace(/&.+/, '')
        : '1.0.0';
      if (
        token &&
        config.url &&
        config.url.indexOf('/login/safe/byPwd') < 0 &&
        config.url.indexOf('/login/byPhoneCode') < 0
      ) {
        config.headers.token = token;
      }
    }
    // 隐藏专区
    /* const str = localStorage.getItem('NAV_INFO_STATE');
    const obj = str ? JSON.parse(str) : {};
    if (obj.isWorldLegend) {
      let infoData = config.method === 'post' ? config.data || {} : config.params || {};
      const arr = urlList.filter((item: urlListJson) => {
        return config.url && config.url.indexOf(item.url) > 0;
      });
      if (arr.length > 0) {
        infoData[`${arr[0].field}`] = arr[0].id;
      }
      config.method === 'post' ? (config.data = infoData) : (config.params = infoData);
    } */
    // 语音操作
    if (config.url && config.url.indexOf('/voice')) {
      if (config.method === 'post') {
        config.data = VoiceFn.convertParams(config.method, config.data);
      } else if (config.method === 'get') {
        config.params = VoiceFn.convertParams(config.method, config.params);
        if (hasBoxAuth) {
          const requestPath = buildRequestPath(url, config.params, method);
          newSignObj.url = decodeURIComponent(requestPath);
        }
      }
    }
    let data;
    if (config.method === 'post') {
      data = config.data || {};
      // 在修改config.data 之前，保存原始请求体用于签名
      const originalData = config.data;
      const typeSign = config.url?.indexOf(TYPE) !== -1;
      // 埋点特殊处理
      if (typeSign) {
        config.data = toSigns(data);
      } else if (config.url?.indexOf('newLog') === -1 && !hasBoxAuth) {
        // 埋点特殊处理 兼容旧证书
        config.data = Fn.toSign(data, isVersion ? signObj : undefined);
      }
      if (hasBoxAuth) {
        // 如果是文件上传，需要提取metaData 对象
        const signData = typeSign ? config.data : originalData;
        const isFileUpload = isFileUploadRequest({ ...config, data: signData });
        const requestBodyStr = getRequestBody(signData, isFileUpload);
        config.headers['X-BOX-AUTHORIZATION'] = await Fn.toSignV2(requestBodyStr, newSignObj);
      }
      if (config.url && config.url.indexOf(AdminConfig.LOGIN_TYPE[0]) > 0) {
        config.data.loginInfo = encodeURIComponent(config.data.loginInfo);
      }
    } else if (config.method === 'get') {
      const isBlank = blankUrls.some(url => config.url?.includes(url));

      if (!isBlank && hasBoxAuth) {
        // GET请求的请求体为空字符串，查询参数已经在signObj.url 中（通过 buildRequestPath 处理）
        config.headers['X-BOX-AUTHORIZATION'] = await Fn.toSignV2({}, newSignObj);
      } else {
        // 兼容旧证书
        data = config.params || {};
        config.params = Fn.toSign(data, isVersion ? signObj : undefined);
        if (config.url?.indexOf('modlist_') !== -1) {
          config.params.time = Date.now();
        }
      }
    }
    return config;
  },
  (error: AxiosError) => {
    Promise.reject(error);
  }
);

// 添加响应拦截器，拦截登录过期或者没有权限
axios.interceptors.response.use(
  (response: AxiosResponse<ResponseData<any>>) => {
    if (!response.data) {
      return Promise.resolve(response);
    }
    if (!response.data.code) {
      return response as any;
    }

    // 登录已过期或者未登录
    if (AdminConfig.LOGIN_EXPIRE.includes(response.data.code)) {
      // const history = createBrowserHistory();
      /* if (!AdminConfig.SPECIFC_ROUTES.includes(history.location.pathname)) {
        window.stop();
      } */
      if (tipInfo.visible) {
        return;
      }
      console.clear();
      console.log(response, 'response11111111111');
      store.dispatch(
        setBaseTipInfo({
          visible: true,
          title: '系统提示',
          message: response.data.msg,
          saveText: '重新登录'
        })
      );
      sessionStorage.setItem('isExpiration', '1');
      return Promise.reject(new Error(response.data.msg));
    }
    // 易盾接口，返回屏蔽
    if (response.config.url && response.config.url.indexOf('/WyAnticheat/riskCheck') > 0) {
      return;
    }

    // 极验滑块
    // if (response.config.url?.indexOf('/homeapi/Homeindex/versionInfo2') > 0) {
    // if (window.location.href.indexOf('/login/account') === -1 && response.data.code === 101000) {
    if (response.data.code === 101000) {
      console.log(response.config);
      let captchaId = '26e753da4a8206359763bcb9bd327eb4';
      let verifyState: any = {};
      return new Promise((resolve, reject) => {
        (window as any).initGeetest4(
          {
            captchaId,
            product: 'bind',
            mask: {
              ouside: false
            }
          },
          (captcha: any) => {
            captcha.onSuccess(() => {
              const result2: any = captcha.getValidate();
              const param: any = {
                captchaId: captchaId,
                captchaOutput: result2.captcha_output,
                genTime: result2.gen_time,
                lotNumber: result2.lot_number,
                passToken: result2.pass_token
              };
              verifyState.state = true;
              // const wArry = CryptoJS.enc.Utf8.parse(param);
              const base64String = Base64.encode(JSON.stringify(param), true);
              // const base64String = CryptoJS.enc.Base64.parse(param);
              // let dataItem =
              //   response.config.method === 'post' ? JSON.parse(response.config.data) : response.config.params;
              // dataItem = { ...dataItem, _captcha_: base64String };
              // response.config.data = dataItem;
              if (response.config.method === 'post') {
                response.config.data = { ...JSON.parse(response.config.data), _captcha_: base64String };
              } else {
                response.config.params = { ...response.config.params, _captcha_: base64String };
              }
              localStorage.setItem('_captcha_', base64String);
              const res = axios.request(response.config);
              captcha.reset(); // 调用该接口进行重置
              return resolve(res);
            });
            // captcha.appendTo(`#captcha-box${props.registerStatus}`);
            captcha.showCaptcha();
          }
        );
      });
    }

    if (response.data.code === 50001) {
      return Promise.reject(new Error(response.data.msg));
    }

    if (needUploadCode[response.data.code] && response.config.url?.indexOf('/log/push/client-log') === -1) {
      domainFn.logError(response, '', true);
    }
    if (response.config && response.config.url?.indexOf('/app/app_init') !== -1) {
      return response;
    }

    const messageCode = response.config.url && response.config.url.indexOf('push/message') > 0;
    // 请求成功，状态不为成功，需要msg时
    if (messageCode && AdminConfig.MESSAGE_ERROR_CODE.includes(response.data.code)) {
      return response as any;
    }

    // 账号实名判断
    if (AdminConfig.REALNAME_CODE.includes(response.data.code)) {
      // window.location.href = '/user/personal/certification';
      if (realnameInfo.visible) {
        return;
      }
      store.dispatch(
        setRealnameInfo({
          visible: true
        })
      );
      return response.data as any;
    }

    const { config } = response;
    // 请求成功根据接口返回所有数据
    if (response.data.code === AdminConfig.SUCCESS_CODE && config.url) {
      for (const item of AdminConfig.SUCCESS_REQUEST_LIST) {
        if (config.url === item) {
          return response as any;
        }
      }
    }

    // 请求成功
    if (response.data.code === AdminConfig.SUCCESS_CODE) {
      return response.data as any;
    }
    const messageCartCheck = response.config.url && response.config.url.indexOf('shoppingCart/cartCheck') > 0;
    // 请求成功，状态不为成功，需要msg时
    if (messageCartCheck && response.data.code !== AdminConfig.SUCCESS_CODE) {
      let error: any = new Error(response.data.msg);
      error.code = response.data.code;
      return Promise.reject(error);
    }
    const messageOrderCheck = response.config.url && response.config.url.indexOf('shoppingCart/orderCheck') > 0;
    // 请求成功，状态不为成功，需要msg时
    if (messageOrderCheck && response.data.code !== AdminConfig.SUCCESS_CODE) {
      let error: any = new Error(response.data.msg);
      error.code = response.data.code;
      return Promise.reject(error);
    }

    // 请求成功，状态不为成功，需要msg时
    if (
      AdminConfig.ERROR_CODE.includes(
        typeof response.data.code === 'number' ? response.data.code : Number(response.data.code)
      )
    ) {
      return response as any;
    }

    const adminCode = response.config.url && response.config.url.indexOf('UserInfo/check_phone_code') > 0;
    // 请求成功，状态不为成功，需要msg时
    if (adminCode && AdminConfig.AMEND_ERROR_CODE.includes(response.data.code)) {
      return response as any;
    }

    // 请求成功，状态不为成功，不需要msg
    // if (AdminConfig.SPECIAL_CODE.includes(response.data.code)) {
    //   return Promise.reject(new Error(response.data.msg));
    // }

    // 请求成功，状态不为成功时
    const rechargeErrorCode =
      response.config.url &&
      (response.config.url.indexOf('PayStatus/Check') > 0 ||
        response.config.url?.indexOf('a1010/v1/queryAwardName') > 0); // 去除金屠龙奖接口返回错误提示
    if (!rechargeErrorCode) message.error(response.data.msg);

    if (response.headers['content-type'] && response.headers['content-type'].indexOf('application/json') === -1) {
      if (response.config) {
        try {
          let json = JSON.stringify(response.data);
          console.log(json);
          // console.log('1232414214');
        } catch (e) {
          if (response.config.url?.indexOf(domainFn.domain) !== -1) {
            domainFn.logError(response, '', true);
            if (domainFn.domain === domainFn.default) {
              domainFn.init().then(() => {
                domainFn.switch(true);
              });
            } else {
              domainFn.switch();
            }
          }
        }
      }
    }

    if (response.status >= 310 && response.status < 400) {
      if (response.config) {
        if (response.config.url?.indexOf(domainFn.domain) !== -1) {
          if (domainFn.domain === domainFn.default) {
            domainFn.init().then(() => {
              domainFn.switch(true);
            });
          } else {
            domainFn.switch();
          }
          domainFn.logError(response);
        }
      }
      (window as any).raw.catchJsError({
        errorInfo: JSON.stringify({
          name: 'request status not 200',
          response
        })
      });
    }
    return Promise.reject(new Error(response.data.msg));
  },
  // only return 4xx 5xx
  (error: AxiosError) => {
    const errorInfo: any = error?.toJSON ? error?.toJSON() : {};
    if (errorInfo?.message === 'Request aborted') {
      return Promise.reject(error);
    }
    console.dir(errorInfo);
    console.log('djsiafndijsaifjdiska=====');
    if (error.config?.url?.indexOf('/log/push/client-log') !== -1) {
      return;
    }
    if (errorInfo?.code === 'ECONNABORTED') {
      domainFn.logError(false, error);
      return;
    }
    // message.error(error.message);
    // 底层网络错误时候处理，例如代理关闭打开时，请求被强制关闭。
    if (error.config) {
      const codeStatus = error?.response?.status ? error?.response?.status : 0;
      if (error.config.url?.indexOf(domainFn.domain) !== -1) {
        if (!error.response || (codeStatus !== 404 && codeStatus < 500)) {
          domainFn.logError(false, error);
          if (domainFn.domain === domainFn.default) {
            domainFn.init().then(() => {
              domainFn.switch(true);
            });
          } else {
            domainFn.switch();
          }
        }
      }
      if (error.config.url?.indexOf('/app/app_init') !== -1) {
        domainFn.logError(false, error);
        return;
      }
    }
    if (/\.\//.test(error.config?.url as string)) {
      if (error.config.url?.indexOf('/log/push/client-log') !== -1) {
        domainFn.logError(false, error);
        return;
      }
      window.location.reload();
    }
    (window as any).raw.catchJsError({
      errorInfo: JSON.stringify({
        name: 'request error',
        error
      })
    });
    return Promise.reject(error);
  }
);

// 统一发起请求的函数
export function request<T>(options: AxiosRequestConfig) {
  return axios.request<T>(options);
}
